import { useQueryClient } from "@tanstack/react-query";
import { ProjectStatus } from "../../../types";
import FetchClients, { ClientOutput, ClientPaths } from "../client";
import { getProjectsInProgressQueryKey } from "./inProgress";
import { useInvalidateEquipmentQueries } from "../equipment/useInvalidateEquipmentQueries";
import { useCurrentAccount } from "@src/backpack-console/CurrentAccountManager";
import {
  BackpackServiceError,
  isBackpackResponseErrorBody,
  throwBackpackResponseError,
  throwBackpackSuperagentResponseError,
} from "../errors";
import { useCallback } from "react";
import request from "@src/global_functions/request";
import attachments from "./projectAttachments";
import { VITE_SITE_SERVICE_BASE_URL } from "@src/env";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";

export type BackpackProject = ClientOutput["projects"]["Project"];
export type BackpackProjectRequest = ClientOutput["projects"]["ProjectRequest"];
export type ProjectNote = ClientOutput["projects"]["NoteWithUserInfo"];
export type PortfolioProjectsSummaryFiltersResponse =
  ClientOutput["projects"]["PortfolioProjectsSummaryFilters"];
export type PortfolioProjectsSummary =
  ClientOutput["projects"]["PortfolioProjectsSummary"];
export type SiteProjectsSummary =
  ClientOutput["projects"]["SiteProjectsSummary"];

export type PortfolioProjectsQueryParamFilters = Omit<
  NonNullable<
    ClientPaths["projects"]["/portfolio/summary"]["get"]["parameters"]["query"]
  >,
  "account_id" | "status" | "sites" | "quarter"
>;

const project = {
  useQueryAllForSite,
  useQueryPortfolioSummaryFilters,
  useQueryAccountSummaryRollup,
  useQueryAccountSitesSummaryRollup,
  useQueryProjectTags,
  attachments,
  mutations: {
    usePost,
    usePatch,
    useDelete,
    useBulkDelete,
    usePostBulkUpdateStatus,
    usePostCsvUpload,
  },
} as const;

export default project;

export const PROJECT_KEY = "projects/project";

// endpoint requires {feature: "Projects", access: "read"}
const getBackpackProjects = async (
  siteId: number | null,
  checkFeatureAccessHeader: CheckFeatureAccessHeader,
  status?: ProjectStatus
) => {
  // query is not enabled if siteId is not a number, so error should never throw here
  if (typeof siteId !== "number") {
    throw new Error("siteId is required");
  }
  const { data, error } = await FetchClients.projects.GET("/", {
    params: {
      query: {
        site_id: siteId,
        project_status: status,
      },
    },
    headers: checkFeatureAccessHeader,
  });
  if (data) return data;
  throw new Error(error);
};

function useQueryAllForSite(siteId: number | null, status?: ProjectStatus) {
  return useQueryWithAccessCheckLog(
    ["Projects", "read"],
    (checkFeatureAccessHeader) =>
      getBackpackProjects(siteId, checkFeatureAccessHeader, status),
    {
      queryKey: [PROJECT_KEY, siteId, status],
      enabled: typeof siteId === "number",
    }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
function usePost(siteId: number) {
  const queryClient = useQueryClient();
  const { accountId } = useCurrentAccount();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  const invalidateEquipmentQueries = useInvalidateEquipmentQueries();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async (body: BackpackProjectRequest, checkFeatureAccessHeader) => {
      const { data, error } = await FetchClients.projects.POST("/", {
        body,
        headers: checkFeatureAccessHeader,
      });
      if (data) return data;
      if (isBackpackResponseErrorBody(error)) {
        throw new BackpackServiceError(error);
      }
      throw new Error(error);
    },
    {
      onSuccess: async (_, req) => {
        await queryClient.invalidateQueries({
          queryKey: [PROJECT_KEY, siteId],
        });
        await invalidatePortfolioSummary();
        await invalidateEquipmentQueries({
          accountId,
          siteId,
          equipmentIds: req.linked_equipment,
        });
      },
      onError: (e) => {
        throw new Error(
          `Unable to create new project for site: ${siteId}
        ${(e as Error)?.message || "Unknown Error"}`
        );
      },
    }
  );
}

export type PutProjectRequest = Partial<BackpackProjectRequest> & {
  id: number; // Project ID
};

// endpoint requires {feature: "Projects", access: "edit"}
function usePatch(accountId?: number) {
  const queryClient = useQueryClient();
  const invalidateEquipmentQueries = useInvalidateEquipmentQueries();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();

  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async ({ id, ...body }: PutProjectRequest, checkFeatureAccessHeader) => {
      const { response, error } = await FetchClients.projects.PATCH("/", {
        params: {
          query: {
            id,
          },
        },
        // @ts-expect-error
        body,
        headers: checkFeatureAccessHeader,
      });
      if (response.ok) return;
      if (isBackpackResponseErrorBody(error)) {
        throw new BackpackServiceError(error);
      }
      throw new Error(error);
    },
    {
      onSuccess: async (_, req) => {
        await queryClient.invalidateQueries({
          queryKey: [PROJECT_KEY, req.site_id],
        });
        await invalidateEquipmentQueries({
          accountId,
          siteId: req.site_id,
          equipmentIds: req.linked_equipment,
        });
        await invalidatePortfolioSummary();
      },
    }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
function useDelete(siteId: number) {
  const queryClient = useQueryClient();
  const queryKey = [PROJECT_KEY, siteId];
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async (id: number, checkFeatureAccessHeader) => {
      const { error, response } = await FetchClients.projects.DELETE("/", {
        params: {
          query: {
            id,
          },
        },
        headers: checkFeatureAccessHeader,
      });
      if (response.ok) return;
      throw new Error(error);
    },
    {
      onMutate: async () => {
        // cancel queries
        await queryClient.cancelQueries({
          queryKey,
        });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey,
        });
        await invalidatePortfolioSummary();
      },
    }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
function useBulkDelete(siteId: number) {
  const queryClient = useQueryClient();
  const queryKey = [PROJECT_KEY, siteId];
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async (ids: number[], checkFeatureAccessHeader) => {
      const { error, response } = await FetchClients.projects.DELETE(
        "/bulk_delete",
        {
          params: {
            query: {
              ids,
            },
          },
          headers: checkFeatureAccessHeader,
        }
      );
      if (response.ok) return;
      throw new Error(error);
    },
    {
      onMutate: async () => {
        // cancel queries
        await queryClient.cancelQueries({
          queryKey,
        });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey,
        });
        await invalidatePortfolioSummary();
      },
    }
  );
}

type ProjectStatusRequest = ClientOutput["projects"]["ProjectStatusRequest"];
// endpoint requires {feature: "Projects", access: "edit"}
function usePostBulkUpdateStatus(accountId?: number) {
  const queryClient = useQueryClient();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();

  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async (statuses: ProjectStatusRequest[], checkFeatureAccessHeader) => {
      const { error, response } = await FetchClients.projects.POST(
        "/bulk-update-status",
        {
          body: statuses,
          headers: checkFeatureAccessHeader,
        }
      );
      if (response.ok) return;
      throw new Error(error);
    },
    {
      onMutate: async () => {
        // cancel queries
        await queryClient.cancelQueries({
          queryKey: [PROJECT_KEY],
        });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [PROJECT_KEY],
        });
        await invalidatePortfolioSummary();
        if (accountId) {
          await queryClient.invalidateQueries({
            queryKey: getProjectsInProgressQueryKey(accountId),
          });
        }
      },
    }
  );
}

const PROJECTS_PORTFOLIO_SUMMARY_KEY = "projects/portfolio-summary";

// endpoint requires {feature: "Projects", access: "read"}
function useQueryPortfolioSummaryFilters(accountId: number) {
  return useQueryWithAccessCheckLog(
    ["Projects", "read"],
    async (checkFeatureAccessHeader) => {
      const { data, error } = await FetchClients.projects.GET(
        "/portfolio/summary/filters",
        {
          params: {
            query: {
              account_id: accountId,
            },
          },
          headers: checkFeatureAccessHeader,
        }
      );

      if (data) return data;
      throw new Error(error);
    },
    {
      queryKey: [PROJECTS_PORTFOLIO_SUMMARY_KEY, accountId, "get-filters"],
    }
  );
}

// endpoint requires {feature: "Projects", access: "read"}
function useQueryAccountSummaryRollup(
  accountId: number,
  filters?: PortfolioProjectsQueryParamFilters
) {
  return useQueryWithAccessCheckLog(
    ["Projects", "read"],
    async (checkFeatureAccessHeader): Promise<PortfolioProjectsSummary> => {
      const { data, error, response } = await FetchClients.projects.GET(
        "/portfolio/summary",
        {
          params: {
            query: {
              account_id: accountId,
              ...filters,
            },
          },
          headers: checkFeatureAccessHeader,
        }
      );

      // 404 is returned in the case that we are filtering by a site id and an asset manager id, wherin the asset manager does not have access to the site
      // This is a workaround to avoid the error from the query
      if (response.status === 404) {
        return {
          energy_saver_projects: 0,
          total_projects: 0,
          total_budgeted_cost: 0,
          projects_with_rewards: 0,
          projects_by_type: [],
        };
      }

      if (data) return data;
      throw new Error(error);
    },
    {
      queryKey: [
        PROJECTS_PORTFOLIO_SUMMARY_KEY,
        accountId,
        "portfolio-rollup",
        filters,
      ],
    }
  );
}

// endpoint requires {feature: "Projects", access: "read"}
function useQueryAccountSitesSummaryRollup(
  accountId: number,
  filters?: PortfolioProjectsQueryParamFilters
) {
  return useQueryWithAccessCheckLog(
    ["Projects", "read"],
    async (checkFeatureAccessHeader): Promise<SiteProjectsSummary[]> => {
      const { data, error, response } = await FetchClients.projects.GET(
        "/portfolio/summary/sites",
        {
          params: {
            query: {
              account_id: accountId,
              ...filters,
            },
          },
          headers: checkFeatureAccessHeader,
        }
      );

      // 404 is returned in the case that we are filtering by a site id and an asset manager id, wherin the asset manager does not have access to the site
      // This is a workaround to avoid the error from the query
      if (response.status === 404) {
        return [];
      }
      if (data) return data;
      throw new Error(error);
    },
    {
      queryKey: [
        PROJECTS_PORTFOLIO_SUMMARY_KEY,
        accountId,
        "sites-rollup",
        filters,
      ],
    }
  );
}

export type ProjectTag = ClientOutput["projects"]["Tag"];

// endpoint requires {feature: "Projects", access: "read"}
function useQueryProjectTags() {
  return useQueryWithAccessCheckLog(
    ["Projects", "read"],
    async (checkFeatureAccessHeader) => {
      const { data, error } = await FetchClients.projects.GET("/tags", {
        headers: checkFeatureAccessHeader,
      });
      if (data) return data;
      throwBackpackResponseError(error);
    },
    {
      queryKey: ["project-tags"],
    }
  );
}

export function useInvalidateProjectsPortfolioSummary() {
  const queryClient = useQueryClient();
  const { accountId } = useCurrentAccount();

  return useCallback(async () => {
    queryClient.invalidateQueries({
      queryKey: [PROJECTS_PORTFOLIO_SUMMARY_KEY, accountId],
    });
  }, [queryClient, accountId]);
}

// endpoint requires {feature: "Projects", access: "edit"}
function usePostCsvUpload(siteId: number) {
  const queryClient = useQueryClient();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async (csv: File, checkFeatureAccessHeader) => {
      try {
        await request
          .post(`${VITE_SITE_SERVICE_BASE_URL}/projects/csv`)
          .set(checkFeatureAccessHeader)
          .query({
            site_id: siteId,
          })
          .send(csv);
      } catch (error) {
        throwBackpackSuperagentResponseError(error);
      }
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [PROJECT_KEY, siteId],
        });
        await invalidatePortfolioSummary();
      },
    }
  );
}
